import React, {
  FormEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

interface PdaScanBridgeWindow extends Window {
  XSOSPdaScan?: (payload: unknown) => void;
  onPdaScanResult?: (payload: unknown) => void;
}

interface PdaScanCaptureProps {
  ariaLabel: string;
  enabled: boolean;
  focusKey?: string;
  onEmptyScan?: () => void;
  onScan: (rawText: string) => void | Promise<void>;
  testId?: string;
}

const AUTO_SUBMIT_DELAY_MS = 400;
const DEDUPE_WINDOW_MS = 1200;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function serializeScanPayload(payload: unknown): string {
  if (typeof payload === 'string') {
    return payload;
  }
  if (typeof payload === 'number') {
    return String(payload);
  }
  try {
    const serialized = JSON.stringify(payload);
    return typeof serialized === 'string' ? serialized : '';
  } catch {
    return '';
  }
}

function extractMessagePayload(value: unknown): unknown {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      const nested = extractMessagePayload(parsed);
      return nested === null ? null : nested === parsed ? value : nested;
    } catch {
      return null;
    }
  }

  const record = asRecord(value);
  if (!record) {
    return null;
  }
  if ('result' in record || 'qrText' in record) {
    return record;
  }
  const type = String(record.type || record.event || '').toLowerCase();
  return ['pda-scan', 'pda_scan', 'xsos:pda-scan', 'xsos_pda_scan'].includes(type)
    ? record.payload ?? record.data ?? null
    : null;
}

function isExternalEditableTarget(target: EventTarget | null, scannerInput: HTMLInputElement | null) {
  if (!(target instanceof HTMLElement) || target === scannerInput) {
    return false;
  }
  return target instanceof HTMLInputElement
    || target instanceof HTMLTextAreaElement
    || target.isContentEditable;
}

function PdaScanCapture({
  ariaLabel,
  enabled,
  focusKey,
  onEmptyScan,
  onScan,
  testId = 'pda-scan-capture',
}: PdaScanCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const onScanRef = useRef(onScan);
  const onEmptyScanRef = useRef(onEmptyScan);
  const submitTimerRef = useRef(0);
  const keyboardTimerRef = useRef(0);
  const inputModeTimerRef = useRef(0);
  const keyboardBufferRef = useRef('');
  const submittingRef = useRef(false);
  const lastSubmittedRef = useRef<{ text: string; time: number } | null>(null);
  const [scanValue, setScanValue] = useState('');
  const [scannerInputMode, setScannerInputMode] = useState<'none' | 'text'>('none');

  onScanRef.current = onScan;
  onEmptyScanRef.current = onEmptyScan;

  const focusInput = useCallback((delayMs = 0) => {
    if (!enabled) {
      return;
    }
    window.setTimeout(() => {
      if (document.activeElement instanceof HTMLSelectElement
        || isExternalEditableTarget(document.activeElement, inputRef.current)) {
        return;
      }
      inputRef.current?.focus({ preventScroll: true });
    }, delayMs);
  }, [enabled]);

  useLayoutEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const timers: number[] = [];
    const hasStartedScanning = () => (
      Boolean(inputRef.current?.value)
      || Boolean(keyboardBufferRef.current)
      || submittingRef.current
    );
    const focusNativeScannerInput = () => {
      const input = inputRef.current;
      if (!input
        || document.activeElement instanceof HTMLSelectElement
        || isExternalEditableTarget(document.activeElement, input)
        || hasStartedScanning()) {
        return;
      }
      input.focus({ preventScroll: true });
      try {
        input.setSelectionRange(input.value.length, input.value.length);
      } catch {
        // Some Android WebViews do not expose selection APIs for scanner inputs.
      }
    };
    const rearmNativeScannerInput = () => {
      const input = inputRef.current;
      if (!input || hasStartedScanning()) {
        return;
      }
      input.blur();
      timers.push(window.setTimeout(focusNativeScannerInput, 24));
    };

    focusNativeScannerInput();
    [120, 420, 900].forEach((delayMs) => {
      timers.push(window.setTimeout(rearmNativeScannerInput, delayMs));
    });

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [enabled, focusKey]);

  const submitRawText = useCallback(async (rawText: string) => {
    window.clearTimeout(submitTimerRef.current);
    const text = rawText.trim();
    if (!enabled || submittingRef.current) {
      return;
    }
    if (!text) {
      onEmptyScan?.();
      focusInput(0);
      return;
    }

    const now = Date.now();
    if (lastSubmittedRef.current?.text === text
      && now - lastSubmittedRef.current.time < DEDUPE_WINDOW_MS) {
      return;
    }
    lastSubmittedRef.current = { text, time: now };
    keyboardBufferRef.current = '';
    submittingRef.current = true;
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setScanValue('');
    try {
      await onScanRef.current(text);
    } finally {
      submittingRef.current = false;
      focusInput(80);
    }
  }, [enabled, focusInput, onEmptyScan]);

  useEffect(() => {
    if (!enabled) {
      keyboardBufferRef.current = '';
      window.clearTimeout(keyboardTimerRef.current);
      return undefined;
    }

    const flushKeyboardBuffer = () => {
      const text = keyboardBufferRef.current;
      keyboardBufferRef.current = '';
      window.clearTimeout(keyboardTimerRef.current);
      if (text) {
        void submitRawText(text);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLSelectElement
        || isExternalEditableTarget(event.target, inputRef.current)
        || event.ctrlKey
        || event.metaKey
        || event.altKey
        || event.isComposing) {
        return;
      }
      if (event.key === 'Enter') {
        // Some PDA scan services emit the terminator before they write the
        // decoded value to the input/native callback. Never let that empty
        // terminator submit the hidden form and surface a false scan error.
        event.preventDefault();
        if (keyboardBufferRef.current) {
          flushKeyboardBuffer();
        }
        return;
      }
      if (event.key === 'Backspace') {
        keyboardBufferRef.current = keyboardBufferRef.current.slice(0, -1);
      } else if (event.key.length === 1) {
        keyboardBufferRef.current += event.key;
      } else {
        return;
      }
      window.clearTimeout(keyboardTimerRef.current);
      keyboardTimerRef.current = window.setTimeout(flushKeyboardBuffer, AUTO_SUBMIT_DELAY_MS);
    };
    const heartbeat = window.setInterval(() => {
      if (!document.hidden
        && !(document.activeElement instanceof HTMLSelectElement)
        && !isExternalEditableTarget(document.activeElement, inputRef.current)
        && document.activeElement !== inputRef.current) {
        inputRef.current?.focus({ preventScroll: true });
      }
    }, 500);

    document.addEventListener('keydown', handleKeyDown, true);
    focusInput(0);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      window.clearInterval(heartbeat);
      window.clearTimeout(keyboardTimerRef.current);
    };
  }, [enabled, focusInput, focusKey, submitRawText]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const restoreFocus = () => focusInput(0);
    const restoreVisibleFocus = () => {
      if (!document.hidden) {
        focusInput(40);
      }
    };

    document.addEventListener('visibilitychange', restoreVisibleFocus);
    window.addEventListener('focus', restoreFocus);
    window.addEventListener('pointerdown', restoreFocus, true);
    return () => {
      document.removeEventListener('visibilitychange', restoreVisibleFocus);
      window.removeEventListener('focus', restoreFocus);
      window.removeEventListener('pointerdown', restoreFocus, true);
    };
  }, [enabled, focusInput]);

  const submitRawTextRef = useRef(submitRawText);
  submitRawTextRef.current = submitRawText;

  useEffect(() => {
    const bridgeWindow = window as PdaScanBridgeWindow;
    const previousNativeHandler = bridgeWindow.onPdaScanResult;
    const previousCompatibleHandler = bridgeWindow.XSOSPdaScan;
    const receive = (payload: unknown) => {
      const text = serializeScanPayload(payload);
      if (!text.trim()) {
        onEmptyScanRef.current?.();
        return;
      }
      void submitRawTextRef.current(text);
    };
    const handleEvent = (event: Event) => receive((event as CustomEvent).detail);
    const handleMessage = (event: MessageEvent) => {
      if (event.source && event.source !== window) {
        return;
      }
      const payload = extractMessagePayload(event.data);
      if (payload !== null) {
        receive(payload);
      }
    };

    bridgeWindow.onPdaScanResult = receive;
    bridgeWindow.XSOSPdaScan = receive;
    window.addEventListener('pda-scan', handleEvent);
    window.addEventListener('xsos:pda-scan', handleEvent);
    window.addEventListener('message', handleMessage);
    return () => {
      if (bridgeWindow.onPdaScanResult === receive) {
        bridgeWindow.onPdaScanResult = previousNativeHandler;
      }
      if (bridgeWindow.XSOSPdaScan === receive) {
        bridgeWindow.XSOSPdaScan = previousCompatibleHandler;
      }
      window.removeEventListener('pda-scan', handleEvent);
      window.removeEventListener('xsos:pda-scan', handleEvent);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => () => {
    window.clearTimeout(submitTimerRef.current);
    window.clearTimeout(keyboardTimerRef.current);
    window.clearTimeout(inputModeTimerRef.current);
  }, []);

  useEffect(() => {
    // The same raw scanner envelope can represent a different business step
    // after the page moves from task loading to formal-label attachment.
    lastSubmittedRef.current = null;
  }, [focusKey]);

  function handleInput(value: string) {
    setScanValue(value);
    window.clearTimeout(submitTimerRef.current);
    if (!enabled || !value.trim()) {
      return;
    }
    if (value.trim().startsWith('{') && value.trim().endsWith('}')) {
      void submitRawText(value);
      return;
    }
    submitTimerRef.current = window.setTimeout(() => void submitRawText(value), AUTO_SUBMIT_DELAY_MS);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const text = inputRef.current?.value || keyboardBufferRef.current;
    if (!text.trim()) {
      focusInput(0);
      return;
    }
    void submitRawText(text);
  }

  return (
    <form
      className={`continuous-pmi-manual${enabled ? '' : ' continuous-pmi-hidden-form--disabled'}`}
      data-testid={testId}
      onSubmit={handleSubmit}
    >
      <label className="continuous-pmi-manual__field">
        <span>扫码内容</span>
        <input
          ref={inputRef}
          aria-label={ariaLabel}
          autoFocus
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          disabled={!enabled}
          inputMode={scannerInputMode}
          onBlur={() => {
            window.clearTimeout(inputModeTimerRef.current);
            setScannerInputMode('none');
            focusInput(40);
          }}
          onChange={(event) => handleInput(event.target.value)}
          onFocus={() => {
            window.clearTimeout(inputModeTimerRef.current);
            inputModeTimerRef.current = window.setTimeout(() => {
              if (document.activeElement === inputRef.current) {
                setScannerInputMode('text');
              }
            }, 80);
          }}
          onInput={(event) => handleInput(event.currentTarget.value)}
          onKeyUp={(event) => {
            if (event.key !== 'Enter') {
              return;
            }
            event.preventDefault();
            const text = event.currentTarget.value || keyboardBufferRef.current;
            if (text.trim()) {
              void submitRawText(text);
            } else {
              focusInput(0);
            }
          }}
          onPaste={(event) => {
            const text = event.clipboardData.getData('text');
            if (text.trim()) {
              event.preventDefault();
              setScanValue(text);
              void submitRawText(text);
            }
          }}
          spellCheck={false}
          value={scanValue}
        />
      </label>
      <button disabled={!scanValue.trim() || !enabled} type="submit">确认</button>
    </form>
  );
}

export default PdaScanCapture;
